import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Copy, Check, User, CreditCard, Car, Home, Clock } from 'lucide-react';
import type { VisitorPass } from '@condo/contracts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';

interface Props {
  pass: VisitorPass;
  className?: string;
}

function statusBadge(status: VisitorPass['status']) {
  switch (status) {
    case 'pending':
      return <Badge variant="warning">Pendiente</Badge>;
    case 'used':
      return <Badge variant="success">Utilizado</Badge>;
    case 'expired':
      return <Badge variant="neutral">Expirado</Badge>;
  }
}

function formatDt(iso: string) {
  return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
}

export function QrDisplay({ pass, className }: Props) {
  const [copied, setCopied] = useState(false);
  const verifyUrl = `${window.location.origin}/verificar/${pass.verificationToken}`;

  async function handleCopy() {
    await navigator.clipboard.writeText(verifyUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className={cn(
        'rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-6',
        className,
      )}
    >
      {/* QR Code */}
      <div className="flex flex-col items-center gap-4">
        <div className="rounded-lg border border-[var(--color-hairline)] bg-white p-4">
          <QRCodeSVG value={verifyUrl} size={200} level="M" />
        </div>
        {statusBadge(pass.status)}
      </div>

      {/* Details */}
      <div className="mt-6 space-y-3">
        <div className="flex items-center gap-3 text-sm">
          <User className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
          <span className="font-medium">{pass.visitorNombre}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <CreditCard className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
          <span>{pass.visitorDni}</span>
        </div>
        {pass.vehiclePatent && (
          <div className="flex items-center gap-3 text-sm">
            <Car className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
            <span>{pass.vehiclePatent}</span>
          </div>
        )}
        <div className="flex items-center gap-3 text-sm">
          <Home className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
          <span>Unidad {pass.unidadNumero}</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Clock className="h-4 w-4 shrink-0 text-[var(--color-ink-muted)]" />
          <span>Expira: {formatDt(pass.expiresAt)}</span>
        </div>
      </div>

      {/* Copy URL button */}
      <div className="mt-6">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <Check className="h-4 w-4" />
              Copiado
            </>
          ) : (
            <>
              <Copy className="h-4 w-4" />
              Copiar enlace de verificacion
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
